import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { config } from './config'
import { useAuth, logout as apiLogout } from './auth'
import { AdminChainTree, buildBlockTree } from './ChainTree'
import { sessionHeaders } from './activity'
import { ModelSelect, NumberField, modelConfidence } from './ui'
import { BlacklistPanel, ParamsPanel } from './PredictionControls'
import { modifiedCount, predictRequestBody } from './predictionRequest'
import { useEditablePlan } from './useEditablePlan'
import { BatchExportView } from './BatchExport'
import { PlanEditor } from './PlanEditor'
import { serializePlanForExport, type ExportSheet } from './exportPlan'
import {
  DEFAULTS,
  type BlacklistRule, type Block, type EditLine, type ModelOption,
  type Params, type PredictResponse, type RefSuggestion, type View,
} from './domain'
// ── Cart ─────────────────────────────────────────────────────────────────────
// One built-and-edited model mix, accumulated from the single view. Holds the
// finalized export sheet plus everything needed to reload the mix back into the
// builder (raw prediction, parameters, and the edited plan snapshot).
type CartItem = {
  id: string
  modelIdx: number
  label: string
  modelGroup: string
  result: PredictResponse
  params: Params
  lines: EditLine[]
  blocks: Block[]
  planN: number
  sheet: ExportSheet
}

// ── Reference-model borrow (ST-20) ──────────────────────────────────────────
// For a low-confidence model, suggest structurally-similar, better-populated
// models whose order history can be pooled in to strengthen the prediction. The
// user accepts a suggestion or overrides with any model.
function ReferencePanel({ targetCodes, models, reference, onPick, onClear, confidence }: {
  targetCodes: string[]
  models: ModelOption[]
  reference: { label: string; codes: string[] } | null
  onPick: (r: { label: string; codes: string[] }) => void
  onClear: () => void
  confidence: 'HIGH' | 'MEDIUM' | 'LOW'
}) {
  const [open, setOpen] = useState(false)
  const [sugs, setSugs] = useState<RefSuggestion[] | null>(null)
  const [loading, setLoading] = useState(false)

  const codesKey = targetCodes.slice().sort().join('+')
  // Reset cached suggestions whenever the target model changes.
  useEffect(() => { setSugs(null); setOpen(false) }, [codesKey])

  async function fetchSugs() {
    if (sugs !== null || loading) return
    setLoading(true)
    try {
      const res = await fetch(`${config.apiUrl}/api/reference-suggestions`, {
        method: 'POST',
        headers: sessionHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ model_codes: targetCodes, limit: 6 }),
      })
      const d = await res.json()
      setSugs(d.suggestions ?? [])
    } catch {
      setSugs([])
    } finally {
      setLoading(false)
    }
  }
  function openPanel() { setOpen(true); fetchSugs() }
  function toggle() { if (open) { setOpen(false) } else { openPanel() } }

  if (reference) {
    return (
      <div className="mb-4 flex flex-wrap items-center gap-2 border border-[#1c69d4] bg-[#EEF4FC] px-4 py-2.5 text-sm">
        <span className="text-[13px]">🔗</span>
        <span className="text-black">Borrowing data from <span className="font-bold">{reference.label}</span></span>
        <button type="button" onClick={openPanel} className="ml-1 text-xs font-bold uppercase tracking-[0.06em] text-[#1c69d4] hover:underline">Change</button>
        <button type="button" onClick={onClear} className="text-xs font-bold uppercase tracking-[0.06em] text-[#666] hover:text-black">Remove</button>
        {open && <SuggestionList sugs={sugs} loading={loading} models={models} onPick={r => { onPick(r); setOpen(false) }} />}
      </div>
    )
  }

  // LOW confidence is an amber prompt to strengthen the prediction; MEDIUM is a neutral,
  // optional offer — both let the user borrow a reference model.
  const isLow = confidence === 'LOW'
  const box = isLow ? 'border-[#E0A200] bg-[#FFF6E0]' : 'border-[#CFD3DB] bg-[#F4F6F9]'
  const text = isLow ? 'text-[#8A5A00]' : 'text-[#555]'
  const btn = isLow
    ? 'border-[#8A5A00] text-[#8A5A00] hover:bg-[#8A5A00] hover:text-white'
    : 'border-[#92A2BD] text-[#555] hover:bg-[#92A2BD] hover:text-white'
  const msg = isLow
    ? 'Not much data for this model — borrow order history from a similar one to strengthen the prediction.'
    : 'Confidence is moderate. Optionally borrow order history from a similar model to firm it up.'
  return (
    <div className={`mb-4 border px-4 py-2.5 ${box}`}>
      <div className={`flex flex-wrap items-center gap-2 text-sm ${text}`}>
        <span>{msg}</span>
        <button
          type="button" onClick={toggle}
          className={`ml-auto border px-3 py-1 text-xs font-bold uppercase tracking-[0.06em] ${btn}`}
        >
          {open ? 'Hide' : 'Suggest reference models'}
        </button>
      </div>
      {open && <SuggestionList sugs={sugs} loading={loading} models={models} onPick={onPick} />}
    </div>
  )
}

function SuggestionList({ sugs, loading, models, onPick }: {
  sugs: RefSuggestion[] | null
  loading: boolean
  models: ModelOption[]
  onPick: (r: { label: string; codes: string[] }) => void
}) {
  const [manualIdx, setManualIdx] = useState(0)
  return (
    <div className="mt-3 w-full space-y-2 border-t border-[#E8D9A8] pt-3">
      {loading && <p className="text-xs text-[#8A5A00]">Finding similar models…</p>}
      {sugs && sugs.length === 0 && !loading && <p className="text-xs text-[#8A5A00]">No suitable reference model found.</p>}
      {sugs && sugs.map(s => (
        <button
          key={s.model_group} type="button"
          onClick={() => onPick({ label: s.label, codes: s.codes })}
          className="flex w-full items-center gap-3 border border-[#E5E7EC] bg-white px-3 py-2 text-left hover:border-[#1c69d4]"
        >
          <span className="text-sm font-bold text-black">{s.label}</span>
          <span className="text-xs text-[#999]">{s.order_count.toLocaleString()} orders</span>
          {s.same_class && <span className="border border-[#1c69d4] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.06em] text-[#1c69d4]">same class</span>}
          <span className="ml-auto text-xs tabular-nums text-[#999]">{Math.round(s.structural_similarity * 100)}% match</span>
        </button>
      ))}
      <div className="flex items-center gap-2 pt-1">
        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#999]">or pick any model</span>
        <div className="min-w-0 flex-1">
          <ModelSelect models={models} value={manualIdx} onChange={i => { setManualIdx(i); onPick({ label: models[i].label, codes: models[i].codes }) }} />
        </div>
      </div>
    </div>
  )
}

// ── Configurator (the admin app) ────────────────────────────────────────────

export default function Configurator() {
  const { user, refresh } = useAuth()
  const navigate = useNavigate()
  async function logout() {
    await apiLogout()
    await refresh()
    navigate('/login', { replace: true })
  }
  // Batch export is hidden for now; the cart supersedes it. Kept in code (the
  // view === 'batch' branch) so it can be restored by re-adding the nav tab.
  const [view] = useState<View>('single')
  const [models, setModels] = useState<ModelOption[]>([])
  const [selectedIdx, setSelectedIdx] = useState(0)
  const [nCars, setNCars] = useState(50)
  const [params, setParams] = useState<Params>({ ...DEFAULTS })
  const [showParams, setShowParams] = useState(false)
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<PredictResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  // NMC is the native representation now; blocks with no edition fall back to legacy
  // display per-block. No user toggle.
  const nmcMode = true
  const [blacklist, setBlacklist] = useState<BlacklistRule[]>([])
  const [showBlacklist, setShowBlacklist] = useState(false)
  const [exploreBlock, setExploreBlock] = useState<Block | null>(null)
  const [reference, setReference] = useState<{ label: string; codes: string[] } | null>(null)
  const prevParams = useRef<Params | null>(null)
  const [cart, setCart] = useState<CartItem[]>([])
  const [cartOpen, setCartOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [cartExporting, setCartExporting] = useState(false)
  const [cartError, setCartError] = useState<string | null>(null)

  // Blacklist ("red cards") is scoped to the selected model and persisted per model
  // in the DB — it loads automatically and is shared across users/sessions.
  const blacklistCodes = models[selectedIdx]?.codes ?? []
  const modelKey = blacklistCodes.slice().sort().join('+')

  // Load the saved rules whenever the selected model changes.
  useEffect(() => {
    if (!modelKey) { setBlacklist([]); return }
    let live = true
    fetch(`${config.apiUrl}/api/blacklist?model_key=${encodeURIComponent(modelKey)}`, { headers: sessionHeaders() })
      .then(r => r.json())
      .then(d => { if (live) setBlacklist(d.rules ?? []) })
      .catch(() => { if (live) setBlacklist([]) })
    return () => { live = false }
  }, [modelKey])

  // A user edit both updates state and persists it (shared, survives reload).
  function saveBlacklist(rules: BlacklistRule[]) {
    setBlacklist(rules)
    if (!modelKey) return
    fetch(`${config.apiUrl}/api/blacklist`, {
      method: 'PUT',
      headers: sessionHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ model_key: modelKey, rules }),
    }).catch(() => {})
  }

  // Editable plan (pin-and-flow, add/remove blocks & configs) — shared with batch.
  const modelCodes = models[selectedIdx]?.codes ?? []
  const plan = useEditablePlan(result, modelCodes)

  // The chain tree for the block being explored — built straight from the prediction.
  const exploreTree = useMemo(
    () => (exploreBlock && result)
      ? buildBlockTree(result.blocks, result.option_descriptions,
          models[selectedIdx]?.description ?? result.model_codes.join(' + '),
          { packages: exploreBlock.packages, single_options: exploreBlock.single_options })
      : null,
    [exploreBlock, result, models, selectedIdx],
  )

  useEffect(() => {
    fetch(`${config.apiUrl}/api/models`)
      .then(r => r.json())
      .then(setModels)
      .catch(() => setError('Could not load models'))
  }, [])

  // Clear any borrowed reference when the target model changes.
  useEffect(() => { setReference(null) }, [selectedIdx])

  // Predict on the selected model's codes plus any reference codes borrowed in
  // (equal pool). refCodes is passed explicitly so a just-accepted reference is
  // used before its state update lands.
  async function runPredict(refCodes: string[]) {
    if (!models.length) return
    setLoading(true)
    setResult(null)
    setError(null)
    prevParams.current = { ...params }
    const codes = [...models[selectedIdx].codes, ...refCodes]
    try {
      const res = await fetch(`${config.apiUrl}/api/predict`, {
        method: 'POST',
        headers: sessionHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(predictRequestBody(codes, nCars, params, blacklist)),
      })
      if (!res.ok) {
        const body = await res.text()
        let detail = body
        try {
          detail = JSON.parse(body).detail ?? body
        } catch {
          // Proxies and unhandled server errors can return plain text instead of JSON.
        }
        throw new Error(detail || `HTTP ${res.status}`)
      }
      setResult(await res.json())
    } catch (err) {
      setError(String(err))
    } finally {
      setLoading(false)
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    runPredict(reference?.codes ?? [])
  }
  function pickReference(r: { label: string; codes: string[] }) {
    setReference(r)
    runPredict(r.codes)
  }
  function clearReference() {
    setReference(null)
    runPredict([])
  }

  // ── Cart ──────────────────────────────────────────────────────────────────
  // Stash the current mix (adding a new item, or updating the one being edited)
  // and reset the builder for the next model.
  function addToCart() {
    if (!result || !models[selectedIdx]) return
    const model = models[selectedIdx]
    const item: CartItem = {
      id: editingId ?? crypto.randomUUID(),
      modelIdx: selectedIdx,
      label: model.label,
      modelGroup: result.model_group,
      result,
      params: { ...params },
      lines: plan.lines,
      blocks: plan.blocks,
      planN: plan.planN,
      sheet: serializePlanForExport(result, model.label, plan),
    }
    setCart(c => (editingId ? c.map(x => (x.id === editingId ? item : x)) : [...c, item]))
    setEditingId(null)
    setResult(null)
    setReference(null)
  }

  // Reload a stored item into the builder; re-adding updates it in place.
  function editCartItem(item: CartItem) {
    setEditingId(item.id)
    setSelectedIdx(item.modelIdx)
    setParams(item.params)
    prevParams.current = { ...item.params }
    setReference(null)
    plan.primeRestore({ lines: item.lines, blocks: item.blocks, planN: item.planN })
    setResult({ ...item.result })
    setCartOpen(false)
  }

  function removeCartItem(id: string) {
    setCart(c => c.filter(x => x.id !== id))
    if (editingId === id) setEditingId(null)
  }

  async function exportCart() {
    if (!cart.length) return
    setCartExporting(true)
    setCartError(null)
    try {
      const res = await fetch(`${config.apiUrl}/api/export/excel`, {
        method: 'POST',
        headers: sessionHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ sheets: cart.map(c => c.sheet), use_nmc: nmcMode }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.detail ?? `HTTP ${res.status}`)
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `Specs_${new Date().toISOString().slice(0, 10)}.xlsx`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      setCartError(String(err))
    } finally {
      setCartExporting(false)
    }
  }

  // Cart items grouped by model variant for the drawer.
  const cartGroups = useMemo(() => {
    const m = new Map<string, CartItem[]>()
    for (const it of cart) {
      const arr = m.get(it.label) ?? []
      arr.push(it)
      m.set(it.label, arr)
    }
    return [...m.entries()]
  }, [cart])
  const cartTotalCars = cart.reduce((s, c) => s + c.sheet.n_cars, 0)

  const modified = modifiedCount(params)

  return (
    <div className="min-h-dvh bg-white flex flex-col">

      {/* BMW Executive Header */}
      <header className="h-14 border-b border-[#E5E7EC] bg-white flex-shrink-0">
        <div className="flex items-center h-full px-6 gap-0">
          {/* Brand lockup */}
          <div className="flex items-center gap-2.5 flex-shrink-0">
            <img src="/bmw-mini-lockup-grey.png" alt="BMW + MINI" className="h-6" />
            <div className="border-l border-[#CFD3DB] pl-3 ml-1">
              <span className="text-[13px] font-bold uppercase tracking-[0.08em] text-black">
                Stock Order Configurator
              </span>
            </div>
          </div>

          <div className="flex-1" />

          {/* Session */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {user && (
              <span className="text-xs text-[#666] tabular-nums">
                {user.name || user.email}
              </span>
            )}
            <button
              type="button" onClick={() => setCartOpen(true)}
              className="relative border border-[#CFD3DB] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-[#666] hover:border-black hover:text-black"
            >
              Cart
              {cart.length > 0 && (
                <span className="ml-1.5 bg-bmw-ocean px-1.5 py-0.5 text-[10px] tabular-nums text-white">
                  {cart.length}
                </span>
              )}
            </button>
            {user?.role === 'admin' && (
              <button
                type="button" onClick={() => navigate('/admin')}
                className="border border-bmw-ocean px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-bmw-ocean hover:bg-bmw-ocean hover:text-white"
              >
                Admin
              </button>
            )}
            <button
              type="button" onClick={logout}
              className="border border-[#CFD3DB] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-[#666] hover:border-black hover:text-black"
            >
              Log out
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 w-full max-w-5xl mx-auto px-6 py-8">

        {view === 'single' && (
          <>
            <form onSubmit={handleSubmit} className="mb-10 border border-[#E5E7EC] bg-white p-6" style={{ borderTop: '2px solid #92A2BD' }}>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">

                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.12em] text-[#777]">Model</label>
                  <ModelSelect models={models} value={selectedIdx} onChange={i => { setSelectedIdx(i); setEditingId(null) }} />
                  {models[selectedIdx] && (
                    <p className="mt-1 text-xs text-[#999]">
                      {models[selectedIdx].description} · codes: {models[selectedIdx].codes.join(', ')}
                    </p>
                  )}
                </div>

                <div>
                  <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.12em] text-[#777]">
                    Number of cars
                  </label>
                  <NumberField
                    min={1} max={500} value={nCars}
                    onCommit={setNCars}
                    className="w-full border border-[#CFD3DB] px-3 py-2 text-sm text-black focus:border-bmw-ocean focus:outline-none rounded-[4px]"
                  />
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="submit" disabled={loading || !models.length}
                  className="bg-bmw-ocean hover:bg-[#024556] text-white px-5 py-2 text-xs font-bold uppercase tracking-[0.06em] rounded-[2px] transition disabled:opacity-40"
                >
                  {loading ? 'Predicting…' : 'Generate'}
                </button>
              </div>

              {/* Parameters + Blacklist — two independent prediction-input sections */}
              <div className="mt-5 border-t border-[#E5E7EC] pt-4">
                <button
                  type="button"
                  onClick={() => setShowParams(v => !v)}
                  className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.06em] text-[#666] hover:text-black"
                >
                  <span className="text-[10px]">{showParams ? '▲' : '▼'}</span>
                  Parameters
                  {modified > 0 && (
                    <span className="border border-bmw-ocean bg-white text-bmw-ocean px-2 py-0.5 text-[10px] font-bold tracking-[0.06em]">
                      {modified} modified
                    </span>
                  )}
                </button>

                {showParams && (
                  <ParamsPanel
                    params={params}
                    onChange={setParams}
                    onReset={() => setParams({ ...DEFAULTS })}
                  />
                )}
              </div>

              {/* Blacklist — scoped to the selected model, persisted per model */}
              <div className="mt-4 border-t border-[#E5E7EC] pt-4">
                <button
                  type="button"
                  onClick={() => setShowBlacklist(v => !v)}
                  className="flex w-full items-center gap-2 text-xs font-bold uppercase tracking-[0.06em] text-[#666] hover:text-black"
                >
                  <span className="text-[10px]">{showBlacklist ? '▲' : '▼'}</span>
                  Blacklist
                  <span className="font-normal normal-case tracking-normal text-[#999]">— exclude configurations from prediction</span>
                  {blacklist.length > 0 && (
                    <span className="bg-black px-2 py-0.5 text-[10px] font-bold tracking-[0.06em] text-white">
                      {blacklist.length} active
                    </span>
                  )}
                </button>

                {/* Always-visible summary of active restrictions, even when collapsed */}
                {blacklist.length > 0 && !showBlacklist && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {blacklist.map((r, i) => (
                      <span key={i} className="inline-flex items-center gap-1 border border-black bg-white px-2 py-0.5 text-[11px]">
                        {r.terms.map((t, j) => (
                          <span key={j} className="inline-flex items-center gap-1">
                            {j > 0 && <span className="text-[9px] font-bold uppercase text-bmw-blue-grey">and</span>}
                            <span className="font-mono text-black">{t.value}</span>
                          </span>
                        ))}
                      </span>
                    ))}
                  </div>
                )}

                {showBlacklist && (
                  <div className="mt-4">
                    <BlacklistPanel modelCodes={blacklistCodes} blacklist={blacklist} onChange={saveBlacklist} />
                  </div>
                )}
              </div>
            </form>

            {error && (
              <div className="mb-6 border border-black bg-bmw-paper p-4 text-sm text-black">
                {error}
              </div>
            )}

            {result && (
              <>
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-baseline gap-3">
                    <h2 className="text-[20px] font-bold uppercase tracking-[0.04em] text-black">
                      {plan.planN} cars · {result.model_codes.join(' + ')}
                    </h2>
                    <span className="text-sm text-[#999]">{result.blocks.length} spec blocks</span>
                    {prevParams.current && modifiedCount(prevParams.current) > 0 && (
                      <span className="border border-[#CFD3DB] bg-bmw-paper px-2.5 py-0.5 text-xs font-bold uppercase tracking-[0.04em] text-[#444]">
                        custom parameters
                      </span>
                    )}
                    {editingId && (
                      <span className="border border-bmw-ocean bg-white px-2.5 py-0.5 text-xs font-bold uppercase tracking-[0.04em] text-bmw-ocean">
                        editing cart item
                      </span>
                    )}
                  </div>
                  <button
                    type="button" onClick={addToCart}
                    className="bg-bmw-ocean hover:bg-[#024556] text-white px-4 py-2 text-xs font-bold uppercase tracking-[0.06em] rounded-[2px] transition"
                  >
                    {editingId ? 'Update cart item' : '+ Add to cart'}
                  </button>
                </div>

                {(reference || modelConfidence(result.blocks).label !== 'HIGH') && (
                  <ReferencePanel
                    targetCodes={models[selectedIdx]?.codes ?? []}
                    models={models}
                    reference={reference}
                    onPick={pickReference}
                    onClear={clearReference}
                    confidence={modelConfidence(result.blocks).label}
                  />
                )}

                <PlanEditor result={result} nmcMode={nmcMode} blacklist={blacklist} plan={plan} onExplore={setExploreBlock} />
              </>
            )}
          </>
        )}

        {view === 'batch' && (
          <>
            <BatchExportView models={models} params={params} nmcMode={nmcMode} onNmcModeChange={() => {}} />

            {/* Parameters toggle (shared) */}
            <div className="mt-6 border border-[#E5E7EC] bg-white p-6" style={{ borderTop: '2px solid #92A2BD' }}>
              <button
                type="button"
                onClick={() => setShowParams(v => !v)}
                className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.06em] text-[#666] hover:text-black"
              >
                <span className="text-[10px]">{showParams ? '▲' : '▼'}</span>
                Prediction parameters
                {modified > 0 && (
                  <span className="border border-bmw-ocean bg-white text-bmw-ocean px-2 py-0.5 text-[10px] font-bold tracking-[0.06em]">
                    {modified} modified
                  </span>
                )}
              </button>
              {showParams && (
                <ParamsPanel
                  params={params}
                  onChange={setParams}
                  onReset={() => setParams({ ...DEFAULTS })}
                />
              )}
            </div>
          </>
        )}

      </main>

      {exploreBlock && result && createPortal(
        <div className="fixed inset-0 z-50 flex flex-col bg-white">
          <div className="flex h-14 flex-shrink-0 items-center justify-between gap-4 border-b border-[#E5E7EC] px-6">
            <div className="flex min-w-0 items-baseline gap-2">
              <span className="flex-shrink-0 text-[13px] font-bold uppercase tracking-[0.06em] text-black">Why this block</span>
              <span className="truncate text-xs text-[#999]">
                {[...exploreBlock.packages, ...exploreBlock.single_options].map(c => result.option_descriptions[c] ?? c).join(' + ') || 'Base car'} · {exploreBlock.n_cars} cars
              </span>
            </div>
            <button
              type="button" onClick={() => setExploreBlock(null)}
              className="flex-shrink-0 border border-[#CFD3DB] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-[#666] hover:border-black hover:text-black"
            >
              Close ✕
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-auto px-6 py-5">
            {exploreTree && <AdminChainTree showControls={false} staticNodes={exploreTree} />}
          </div>
        </div>,
        document.body,
      )}

      {cartOpen && createPortal(
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/30"
          onClick={() => setCartOpen(false)}
        >
          <div
            className="flex h-full w-full max-w-md flex-col bg-white shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex h-14 flex-shrink-0 items-center justify-between gap-4 border-b border-[#E5E7EC] px-5">
              <div className="flex items-baseline gap-2">
                <span className="text-[13px] font-bold uppercase tracking-[0.06em] text-black">Cart</span>
                <span className="text-xs text-[#999]">
                  {cart.length} item{cart.length !== 1 ? 's' : ''} · {cartTotalCars} cars
                </span>
              </div>
              <button
                type="button" onClick={() => setCartOpen(false)}
                className="flex-shrink-0 border border-[#CFD3DB] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-[#666] hover:border-black hover:text-black"
              >
                Close ✕
              </button>
            </div>

            {/* Items grouped by model */}
            <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
              {cart.length === 0 ? (
                <p className="mt-8 text-center text-sm text-[#999]">
                  Your cart is empty. Build a mix in Single Prediction and add it here.
                </p>
              ) : (
                <div className="space-y-5">
                  {cartGroups.map(([label, items]) => (
                    <div key={label}>
                      <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.12em] text-[#777]">{label}</p>
                      <div className="space-y-2">
                        {items.map(item => (
                          <div
                            key={item.id}
                            className={`flex items-center justify-between gap-3 border bg-white px-3 py-2.5 ${editingId === item.id ? 'border-bmw-ocean' : 'border-[#E5E7EC]'}`}
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-bold text-black tabular-nums">{item.sheet.n_cars} cars</p>
                              <p className="truncate text-xs text-[#999]">
                                {item.sheet.blocks.length} spec block{item.sheet.blocks.length !== 1 ? 's' : ''} · {item.result.model_codes.join(', ')}
                              </p>
                            </div>
                            <div className="flex flex-shrink-0 items-center gap-1.5">
                              <button
                                type="button" onClick={() => editCartItem(item)}
                                className="border border-[#CFD3DB] px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.06em] text-[#666] hover:border-black hover:text-black"
                              >
                                Edit
                              </button>
                              <button
                                type="button" onClick={() => removeCartItem(item.id)}
                                aria-label="Remove item"
                                className="flex h-7 w-7 items-center justify-center text-[#999] hover:bg-[#E8EBF1] hover:text-black"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer / export */}
            <div className="flex-shrink-0 border-t border-[#E5E7EC] px-5 py-4">
              {cartError && (
                <div className="mb-3 border border-black bg-bmw-paper p-2.5 text-xs text-black">{cartError}</div>
              )}
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={exportCart}
                  disabled={cartExporting || cart.length === 0}
                  className="bg-bmw-ocean hover:bg-[#024556] text-white px-4 py-2 text-xs font-bold uppercase tracking-[0.06em] rounded-[2px] transition disabled:opacity-40"
                >
                  {cartExporting ? 'Generating…' : 'Export cart to Excel'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* BMW Footer */}
      <footer className="flex-shrink-0">
        <div className="border-t border-bmw-blue-grey" />
        <div className="flex items-center justify-between px-6 py-3 text-[11px] text-[#666]">
          <div className="flex items-center gap-3">
            <img src="/bmw-mini-lockup-grey.png" alt="BMW + MINI" className="h-5" />
          </div>
          <span className="uppercase font-bold tracking-[0.06em] text-black text-[11px]">
            Powered by Solvigo
          </span>
        </div>
      </footer>

    </div>
  )
}
